// PodDetails component - Shows detailed information about a selected POD

import React from 'react';
import { MapPin, Navigation, Building2, DollarSign, Calendar, X } from 'lucide-react';
import Card, { CardHeader, CardTitle, CardContent } from '../ui/Card';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import { formatCurrency, formatDate, formatDistance } from '../../utils/formatters';
import { PROVINCES, STATUS_COLORS } from '../../utils/constants';

const PodDetails = ({ pod, outlets = [], onClose, className = '' }) => {
  if (!pod) {
    return (
      <Card className={`text-center py-8 ${className}`}>
        <div className="text-dark-400">
          <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Select a POD to view details</p>
        </div>
      </Card>
    );
  }

  const province = PROVINCES[pod.province];
  const nearbyOutlets = outlets.filter((o) => o.distance && o.distance <= 2000);
  const nearestOutlet = nearbyOutlets.sort((a, b) => a.distance - b.distance)[0];

  return (
    <Card className={className}>
      {/* Header */}
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: province?.color || '#1E3A5F' }}
            />
            {pod.siteNumber}
          </CardTitle>
          <p className="text-sm text-dark-500 mt-1">
            {pod.area}, {province?.name || pod.province}
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-dark-400 hover:text-dark-600 hover:bg-dark-100"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Status Badge */}
        <div className="flex items-center gap-2">
          <Badge
            variant={STATUS_COLORS[pod.status] || 'default'}
            dot
          >
            {pod.status || 'Unknown'}
          </Badge>
          {pod.unitPrice > 0 && (
            <Badge variant="secondary">
              {formatCurrency(pod.unitPrice)}/month
            </Badge>
          )}
        </div>

        {/* Address */}
        <div className="flex items-start gap-3 p-3 bg-dark-50 rounded-lg">
          <MapPin className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-dark-900">{pod.streetAddress}</p>
            <p className="text-sm text-dark-500">{pod.area}, {province?.name || pod.province}</p>
          </div>
        </div>

        {/* Coordinates */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-dark-50 rounded-lg">
            <p className="text-xs text-dark-500 mb-1">Latitude</p>
            <p className="font-mono text-sm text-dark-900">
              {pod.latitude.toFixed(6)}
            </p>
          </div>
          <div className="p-3 bg-dark-50 rounded-lg">
            <p className="text-xs text-dark-500 mb-1">Longitude</p>
            <p className="font-mono text-sm text-dark-900">
              {pod.longitude.toFixed(6)}
            </p>
          </div>
        </div>

        {/* Nearest Outlet */}
        {nearestOutlet && (
          <div className="p-3 border border-accent-200 bg-accent-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-accent-600" />
              <span className="text-sm font-medium text-accent-700">Nearest Fast Food</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg">{nearestOutlet.brandIcon}</span>
                <div>
                  <p className="font-medium text-dark-900">{nearestOutlet.name}</p>
                  <p className="text-xs text-dark-500">{nearestOutlet.brand}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-accent-600">
                  {formatDistance(nearestOutlet.distance)}
                </p>
                <p className="text-xs text-dark-500">
                  {nearestOutlet.rating?.toFixed(1)} ⭐
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Coverage Stats */}
        {nearbyOutlets.length > 0 && (
          <div>
            <p className="text-sm font-medium text-dark-700 mb-2">Nearby Outlets</p>
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center p-2 bg-primary-50 rounded-lg">
                <p className="text-lg font-bold text-primary-600">
                  {nearbyOutlets.filter((o) => o.distance <= 500).length}
                </p>
                <p className="text-xs text-primary-500">500m</p>
              </div>
              <div className="text-center p-2 bg-secondary-50 rounded-lg">
                <p className="text-lg font-bold text-secondary-600">
                  {nearbyOutlets.filter((o) => o.distance <= 1000).length}
                </p>
                <p className="text-xs text-secondary-500">1km</p>
              </div>
              <div className="text-center p-2 bg-accent-50 rounded-lg">
                <p className="text-lg font-bold text-accent-600">
                  {nearbyOutlets.filter((o) => o.distance <= 2000).length}
                </p>
                <p className="text-xs text-accent-500">2km</p>
              </div>
            </div>
          </div>
        )}

        {/* Additional Info */}
        {pod.date && (
          <div className="flex items-center gap-2 text-sm text-dark-500">
            <Calendar className="w-4 h-4" />
            <span>Installed: {formatDate(pod.date)}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="small"
            icon={Navigation}
            className="flex-1"
            onClick={() => {
              const url = `https://www.google.com/maps/dir/?api=1&destination=${pod.latitude},${pod.longitude}`;
              window.open(url, '_blank');
            }}
          >
            Directions
          </Button>
          <Button
            variant="primary"
            size="small"
            className="flex-1"
            onClick={() => {
              // Generate report functionality
              console.log('Generate report for', pod.siteNumber);
            }}
          >
            Generate Report
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PodDetails;
